/**
 * V2 Currency API Store
 * V1 Coin/Token Store를 대체
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { currencyAPIService } from '@/networks/api/v2'
import type { ICurrency, ICurrencyPrice } from '@/types/currency'

export const useCurrencyAPIStore = defineStore('currencyAPI', () => {
  // State
  const currencyList = ref<ICurrency[]>([])
  const currencyPrices = ref<Map<string, ICurrencyPrice>>(new Map())
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchTime = ref<number>(0)

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000

  /**
   * Currency 목록 조회 (V2)
   * coinId(number) 대신 currencyId(string) 사용
   */
  const fetchCurrencyList = async (params?: {
    currencyIds?: string[]
    type?: 'native' | 'token'
    force?: boolean
  }) => {
    // 캐시 체크 (type이 지정되지 않은 경우에만)
    const now = Date.now()
    if (
      !params?.force &&
      !params?.type &&
      currencyList.value.length > 0 &&
      now - lastFetchTime.value < CACHE_DURATION
    ) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const currencies = await currencyAPIService.getCurrencyList({
        currencyIds: params?.currencyIds,
        type: params?.type,
      })

      // 응답이 배열인지 확인
      if (Array.isArray(currencies)) {
        // type이 지정되면 기존 목록에 병합 (다른 type 유지)
        if (params?.type) {
          const existingOtherType = currencyList.value.filter(
            (c) => c.type !== params.type,
          )
          const newCurrencies = currencies.filter(
            (c) => !existingOtherType.some((e) => e.id === c.id),
          )
          currencyList.value = [...existingOtherType, ...newCurrencies]
        } else {
          currencyList.value = currencies
        }
      } else {
        if (!params?.type) {
          currencyList.value = []
        }
      }
      lastFetchTime.value = now
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to fetch currencies'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Currency ID로 검색 (V2)
   * string ID 사용 ('ethereum', 'bitcoin', 'ETHEREUM', 'BITCOIN')
   * 대소문자 무시
   */
  const getCurrencyById = computed(() => (currencyId: string) => {
    const searchId = currencyId?.toUpperCase()
    return currencyList.value.find(
      (currency) => currency.id?.toUpperCase() === searchId,
    )
  })

  /**
   * Symbol로 Currency 검색
   */
  const getCurrencyBySymbol = computed(() => (symbol: string) => {
    return currencyList.value.find(
      (currency) => currency.symbol?.toLowerCase() === symbol.toLowerCase(),
    )
  })

  /**
   * 네트워크별 Currency 검색
   */
  const getCurrenciesByNetwork = computed(() => (network: string) => {
    return currencyList.value.filter((currency) => currency.network === network)
  })

  /**
   * 네이티브 코인만 필터링
   */
  const getNativeCoins = computed(() => {
    return currencyList.value.filter((c) => c.type === 'native')
  })

  /**
   * 토큰만 필터링
   */
  const getTokens = computed(() => {
    return currencyList.value.filter((c) => c.type === 'token')
  })

  /**
   * 특정 체인의 토큰 필터링
   */
  const getTokensByChain = computed(() => (parentCurrencyId: string) => {
    return currencyList.value.filter(
      (c) => c.type === 'token' && c.parentCurrencyId === parentCurrencyId,
    )
  })

  /**
   * Currency 가격 조회 (V2)
   */
  const fetchCurrencyPrices = async (currencyIds: string[]) => {
    isLoading.value = true
    error.value = null

    try {
      const prices = await currencyAPIService.getCurrencyPrices(currencyIds)

      // Map에 저장
      prices.forEach((price) => {
        if (price.currencyId) {
          currencyPrices.value.set(price.currencyId, price)
        }
      })
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to fetch prices'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 단일 Currency 가격 조회
   */
  const getCurrencyPrice = computed(() => (currencyId: string) => {
    return currencyPrices.value.get(currencyId)
  })

  /**
   * 특정 Currency의 토큰 목록 조회
   */
  const fetchTokensForCurrency = async (parentCurrencyId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const tokens =
        await currencyAPIService.getTokensByCurrency(parentCurrencyId)

      // 기존 목록에 병합
      const existingIds = new Set(currencyList.value.map((c) => c.id))
      const newTokens = tokens.filter((t) => !existingIds.has(t.id))

      if (newTokens.length > 0) {
        currencyList.value = [...currencyList.value, ...newTokens]
      }
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to fetch tokens'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Store 초기화
   */
  const resetStore = () => {
    currencyList.value = []
    currencyPrices.value.clear()
    isLoading.value = false
    error.value = null
    lastFetchTime.value = 0
  }

  return {
    // State
    currencyList,
    currencyPrices,
    isLoading,
    error,

    // Actions
    fetchCurrencyList,
    fetchCurrencyPrices,
    fetchTokensForCurrency,
    resetStore,

    // Getters
    getCurrencyById,
    getCurrencyBySymbol,
    getCurrenciesByNetwork,
    getNativeCoins,
    getTokens,
    getTokensByChain,
    getCurrencyPrice,
  }
})
